/**
 * Production-Safe Logger
 * 
 * Replaces console.log with proper logging that:
 * - Only logs in development mode
 * - Sanitizes sensitive data
 * - Uses errorManager for errors in production
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.log('Message', data);
 *   logger.error('Error', error);
 */

import { devLog, devError, devWarn, logError as errorManagerLog } from './secureLogger';
import { logError, logWarning, logInfo } from './errorManager';

const isDevelopment = import.meta.env.DEV;

class Logger {
  /**
   * Log general information (development only)
   */
  log(...args: any[]): void {
    if (isDevelopment) {
      devLog(...args);
    }
  }

  /**
   * Log errors (uses errorManager in production)
   */
  error(message: string, error?: any, context?: any): void {
    if (isDevelopment) {
      devError(message, error, context);
    } else {
      // Use errorManager for production error tracking
      logError(error instanceof Error ? error : new Error(message), {
        component: context?.component,
        action: context?.action,
        additionalData: context
      });
    }
  }

  /**
   * Log warnings
   */
  warn(message: string, context?: any): void {
    if (isDevelopment) {
      devWarn(message, context);
    } else {
      logWarning(message, {
        additionalData: context
      });
    }
  }

  /**
   * Log info messages
   */
  info(message: string, context?: any): void {
    if (isDevelopment) {
      devLog(message, context);
    } else {
      logInfo(message, {
        additionalData: context
      });
    }
  }

  /**
   * Log debug information (development only, completely silent in production)
   */
  debug(...args: any[]): void {
    if (isDevelopment) {
      console.log('[DEBUG]', ...args);
    }
  }
}

export const logger = new Logger();
